/**
 * Shell-level view-state persistence.
 *
 * The shell already provides a view registry + lazy chunks, but historically
 * each module had to wire its own localStorage layer to remember an operator's
 * sort / filter / pagination / column-layout choices for a view. That meant
 * every module re-implemented (often inconsistently, sometimes not at all) the
 * same "remember my view preferences" behaviour.
 *
 * This module is the single shell-level store. A view opts in by declaring a
 * `viewStateKey` in its registered `UiViewDefinition`; ModuleView then hands
 * the rendered component a `viewState` accessor (get/set/clear) bound to a
 * localStorage slot scoped by tenant + user + viewStateKey. Persistence is in
 * localStorage (survives reloads, unlike the sessionStorage drill-through
 * stack) and is namespaced so one tenant/operator's saved view never leaks
 * into another's.
 *
 * Components receive `viewState` as a prop (mirroring how they already receive
 * `entityId`), keeping modules fully decoupled from the shell — no module has
 * to import shell internals.
 */

export interface ViewStateAccessor {
  /** The opt-in key this accessor is bound to (undefined → no-op accessor). */
  readonly viewStateKey?: string;
  /** Read the persisted state, or the supplied fallback when none/invalid. */
  get<T>(fallback: T): T;
  /** Persist the state (shallow JSON-serialised). */
  set<T>(value: T): void;
  /** Merge a partial patch into the persisted object state. */
  patch<T extends Record<string, unknown>>(partial: Partial<T>): void;
  /** Remove the persisted state. */
  clear(): void;
}

const KEY_PREFIX = 'tyndo.viewState';

export interface ViewStateScope {
  tenantId?: string | null;
  userId?: string | null;
}

function slot(scope: ViewStateScope, viewStateKey: string): string {
  const t = scope.tenantId || 'no-tenant';
  const u = scope.userId || 'anon';
  return `${KEY_PREFIX}:${t}:${u}:${viewStateKey}`;
}

/** A no-op accessor for views that didn't opt in (no viewStateKey). */
const NOOP_ACCESSOR: ViewStateAccessor = {
  viewStateKey: undefined,
  get: (fallback) => fallback,
  set: () => {
    /* no-op: the view declared no viewStateKey, so nothing persists */
  },
  patch: () => {
    /* no-op: the view declared no viewStateKey, so nothing persists */
  },
  clear: () => {
    /* no-op: the view declared no viewStateKey, so nothing persists */
  },
};

/**
 * Build a persistence accessor for a view. Returns a no-op accessor when the
 * view didn't declare a `viewStateKey`, so the rendered component can call
 * `viewState.get(...)` unconditionally.
 */
export function createViewStateAccessor(
  scope: ViewStateScope,
  viewStateKey: string | undefined,
): ViewStateAccessor {
  if (!viewStateKey || typeof window === 'undefined') {
    return NOOP_ACCESSOR;
  }
  const key = slot(scope, viewStateKey);
  return {
    viewStateKey,
    get<T>(fallback: T): T {
      try {
        const raw = window.localStorage.getItem(key);
        if (raw == null) return fallback;
        return JSON.parse(raw) as T;
      } catch {
        return fallback;
      }
    },
    set<T>(value: T): void {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* quota / private mode — degrade silently */
      }
    },
    patch<T extends Record<string, unknown>>(partial: Partial<T>): void {
      try {
        const raw = window.localStorage.getItem(key);
        const current = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
        window.localStorage.setItem(key, JSON.stringify({ ...current, ...partial }));
      } catch {
        /* ignore */
      }
    },
    clear(): void {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}
